import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import {
    InsuranceApiKey,
    InsuranceWebhook,
    InsuranceWebhookInput,
} from '@evidentid/tprm-portal-lib/models/api-settings';
import { InsuranceWebhookType } from '@evidentid/tprm-portal-lib/models/api-settings/InsuranceWebhookType.model';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { VuexCommonEntityStatus } from '@/models/VuexCommonEntityStatus.model';
import { VuexCommonListStatus } from '@/models/VuexCommonListStatus.model';
import {
    mutateCommonEntityFailState,
    mutateCommonEntityFinishState,
    mutateCommonEntityStartState,
} from '@/utils/vuex-common/commonEntityMutations';
import {
    mutateCommonListFailState,
    mutateCommonListFinishState,
    mutateCommonListStartState,
} from '@/utils/vuex-common/commonListMutations';
import {
    mutateCommonStatusFailState,
    mutateCommonStatusFinishState,
    mutateCommonStatusStartState,
} from '@/utils/vuex-common/commonStatusMutations';
import { performCommonCudAction } from '@/utils/vuex-common/performCommonCudAction';
import { performCommonGetAction } from '@/utils/vuex-common/performCommonGetAction';

export interface ApiSettingsRequirement {
    gatewayClient: GatewayApiClient;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): ApiSettingsRequirement {
    return store.$app;
}

interface ApiSettingsState {
    apiKey: Record<string, VuexCommonEntityStatus<InsuranceApiKey>>;
    webhooks: Record<string, VuexCommonListStatus<InsuranceWebhook>>;
    webhookTypes: Record<string, VuexCommonListStatus<InsuranceWebhookType>>;
    createWebhookStatus: Record<string, OperationStatus>;
    patchWebhookStatus: Record<string, OperationStatus>;
    deleteWebhookStatus: Record<string, OperationStatus>;
}

export const useApiSettingsStore = defineStore('apiSettings', {
    state: (): ApiSettingsState => ({
        apiKey: {},
        webhooks: {},
        webhookTypes: {},
        createWebhookStatus: {},
        patchWebhookStatus: {},
        deleteWebhookStatus: {},
    }),
    actions: {
        startLoadingApiKey(payload: { rpName: string }) {
            mutateCommonEntityStartState<InsuranceApiKey>(this, 'apiKey', payload);
        },
        finishLoadingApiKey(payload: { rpName: string, data: InsuranceApiKey }) {
            mutateCommonEntityFinishState<InsuranceApiKey>(this, 'apiKey', payload);
        },
        failLoadingApiKey(payload: { rpName: string }) {
            mutateCommonEntityFailState<InsuranceApiKey>(this, 'apiKey', payload);
        },
        startLoadingWebhooks(payload: { rpName: string }) {
            mutateCommonListStartState<InsuranceWebhook>(this, 'webhooks', payload);
        },
        finishLoadingWebhooks(payload: { rpName: string, list: InsuranceWebhook[] }) {
            mutateCommonListFinishState<InsuranceWebhook>(this, 'webhooks', payload);
        },
        failLoadingWebhooks(payload: { rpName: string }) {
            mutateCommonListFailState<InsuranceWebhook>(this, 'webhooks', payload);
        },
        startLoadingWebhookTypes(payload: { rpName: string }) {
            mutateCommonListStartState<InsuranceWebhookType>(this, 'webhookTypes', payload);
        },
        finishLoadingWebhookTypes(payload: { rpName: string, list: InsuranceWebhookType[] }) {
            mutateCommonListFinishState<InsuranceWebhookType>(this, 'webhookTypes', payload);
        },
        failLoadingWebhookTypes(payload: { rpName: string }) {
            mutateCommonListFailState<InsuranceWebhookType>(this, 'webhookTypes', payload);
        },
        startCreatingWebhook(payload: { rpName: string }) {
            mutateCommonStatusStartState(this, 'createWebhookStatus', payload);
        },
        finishCreatingWebhook(payload: { rpName: string, data: InsuranceWebhook }) {
        // adding the successful created webhook to the store without calling get all webhooks
            this.webhooks = {
                ...this.webhooks,
                [payload.rpName]: {
                    ...this.webhooks[payload.rpName],
                    status: OperationStatus.success,
                    list: [ ...this.webhooks[payload.rpName].list, payload.data ],
                },
            };
            mutateCommonStatusFinishState(this, 'createWebhookStatus', payload);
        },
        failCreatingWebhook(payload: { rpName: string }) {
            mutateCommonStatusFailState(this, 'createWebhookStatus', payload);
        },
        startPatchingWebhook(payload: { rpName: string }) {
            mutateCommonStatusStartState(this, 'patchWebhookStatus', payload);
        },
        finishPatchingWebhook(payload: { rpName: string, data: InsuranceWebhook }) {
        // updating the successful patched webhook in the store without calling get all webhooks
            const currentList = this.webhooks[payload.rpName].list;
            const index = currentList.findIndex((x) => x.id === payload.data.id);
            this.webhooks = {
                ...this.webhooks,
                [payload.rpName]: {
                    ...this.webhooks[payload.rpName],
                    status: OperationStatus.success,
                    list: [
                        ...currentList.slice(0, index),
                        payload.data,
                        ...currentList.slice(index + 1) ],
                },
            };
            mutateCommonStatusFinishState(this, 'patchWebhookStatus', payload);
        },
        failPatchingWebhook(payload: { rpName: string }) {
            mutateCommonStatusFailState(this, 'patchWebhookStatus', payload);
        },
        startDeletingWebhook(payload: { rpName: string }) {
            mutateCommonStatusStartState(this, 'deleteWebhookStatus', payload);
        },
        finishDeletingWebhook(payload: { rpName: string, deletedId: string }) {
        // remove the successful deleted webhook in the store without calling get all webhooks
            const currentList = this.webhooks[payload.rpName].list;
            const deletedIndex = currentList.findIndex((x) => x.id === payload.deletedId);
            this.webhooks = {
                ...this.webhooks,
                [payload.rpName]: {
                    ...this.webhooks[payload.rpName],
                    status: OperationStatus.success,
                    list: [ ...currentList.slice(0, deletedIndex), ...currentList.slice(deletedIndex + 1) ],
                },
            };
            mutateCommonStatusFinishState(this, 'deleteWebhookStatus', payload);
        },
        failDeletingWebhook(payload: { rpName: string }) {
            mutateCommonStatusFailState(this, 'deleteWebhookStatus', payload);
        },

        async loadApiKey(payload: {
            rpName: string;
        }) {
            const { rpName } = payload;
            const mutateStart = () => this.startLoadingApiKey({ rpName });
            const mutateFinish = (data: InsuranceApiKey) => this.finishLoadingApiKey({ rpName, data });
            const mutateFail = () => this.failLoadingApiKey({ rpName });
            const getDataCall = () => appOf(this).gatewayClient.tprmUi().getTprmApiKey(rpName);
            await performCommonGetAction<InsuranceApiKey>(
                { state: this }, 'apiKey', rpName, getDataCall, mutateStart, mutateFinish, mutateFail,
            );
        },
        async loadWebhooks(payload: {
            rpName: string;
        }) {
            const { rpName } = payload;
            const mutateStart = () => this.startLoadingWebhooks({ rpName });
            const mutateFinish = (list: InsuranceWebhook[]) => this.finishLoadingWebhooks({ rpName, list });
            const mutateFail = () => this.failLoadingWebhooks({ rpName });
            const getDataCall = () => appOf(this).gatewayClient.tprmUi().getInsuranceWebhooks(rpName);
            await performCommonGetAction<InsuranceWebhook>(
                { state: this }, 'webhooks', rpName, getDataCall, mutateStart, mutateFinish, mutateFail, true,
            );
        },
        async loadWebhookTypes(payload: {
            rpName: string;
        }) {
            const { rpName } = payload;
            const mutateStart = () => this.startLoadingWebhookTypes({ rpName });
            const mutateFinish = (list: InsuranceWebhookType[]) => this.finishLoadingWebhookTypes({
                rpName,
                list,
            });
            const mutateFail = () => this.failLoadingWebhookTypes({ rpName });
            const getDataCall = () => appOf(this).gatewayClient.tprmUi().getWebhookTypes();
            await performCommonGetAction<InsuranceWebhookType>(
                { state: this }, 'webhookTypes', rpName, getDataCall, mutateStart, mutateFinish, mutateFail, true,
            );
        },
        async createWebhook(payload: {
            rpName: string;
            data: InsuranceWebhookInput;
        }) {
            const { rpName, data } = payload;
            const mutateStart = () => this.startCreatingWebhook({ rpName });
            const mutateFinish = (data: InsuranceWebhook) => this.finishCreatingWebhook({ rpName, data });
            const mutateFail = () => this.failCreatingWebhook({ rpName });
            const actionCall = () => appOf(this).gatewayClient.tprmUi().createInsuranceWebhook(rpName, data);
            await performCommonCudAction(
                { state: this }, 'createWebhookStatus', rpName, actionCall, mutateStart, mutateFinish, mutateFail,
            );
        },
        async patchWebhook(payload: {
            rpName: string;
            data: InsuranceWebhook;
        }) {
            const { rpName, data } = payload;
            const mutateStart = () => this.startPatchingWebhook({ rpName });
            const mutateFinish = (data: InsuranceWebhook) => this.finishPatchingWebhook({ rpName, data });
            const mutateFail = () => this.failPatchingWebhook({ rpName });
            const actionCall = () => appOf(this).gatewayClient.tprmUi().patchInsuranceWebhook(rpName, data);
            await performCommonCudAction(
                { state: this }, 'patchWebhookStatus', rpName, actionCall, mutateStart, mutateFinish, mutateFail,
            );
        },
        async deleteWebhook(payload: {
            rpName: string;
            data: InsuranceWebhook;
        }) {
            const { rpName, data } = payload;
            const mutateStart = () => this.startDeletingWebhook({ rpName });
            const mutateFinish = () => this.finishDeletingWebhook({ rpName, deletedId: data.id });
            const mutateFail = () => this.failDeletingWebhook({ rpName });
            const actionCall = () => appOf(this).gatewayClient.tprmUi().deleteInsuranceWebhook(rpName, data.id);
            await performCommonCudAction(
                { state: this }, 'deleteWebhookStatus', rpName, actionCall, mutateStart, mutateFinish, mutateFail,
            );
        },
        clearApiKey(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.apiKey = omit(this.apiKey, [ rpName ]);
        },
        clearWebhooks(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.webhooks = omit(this.webhooks, [ rpName ]);
        },
        clearWebhookTypes(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.webhookTypes = omit(this.webhookTypes, [ rpName ]);
        },
        clearCreatingWebhookStatus(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.patchWebhookStatus = omit(this.patchWebhookStatus, [ rpName ]);
        },
        clearPatchingWebhookStatus(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.patchWebhookStatus = omit(this.patchWebhookStatus, [ rpName ]);
        },
        clearDeletingWebhookStatus(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.deleteWebhookStatus = omit(this.deleteWebhookStatus, [ rpName ]);
        },
    },
});
